import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Image as ImageIcon, Upload, Trash2, RefreshCw } from "lucide-react";
import {
  api,
  apiBase,
  type ProjectImage,
  type ImageVisibility,
} from "@/lib/api";
import { imageSignedDownloadPath } from "@/lib/platformPaths";
import { EmptyState, ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ROUTES } from "@/routes";

const VIS: ImageVisibility[] = ["public", "authenticated", "owner", "signed"];

export function ImagesPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [selected, setSelected] = useState<ProjectImage | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [visibility, setVisibility] = useState<ImageVisibility>("owner");
  const [alt, setAlt] = useState("");
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.listImages(id, {
        status: statusFilter || undefined,
      });
      setImages(data ?? []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll while any image is pending/processing.
  useEffect(() => {
    const pending = images.some(
      (i) => i.status === "pending" || i.status === "processing",
    );
    if (!pending) return;
    const t = setInterval(() => void load(), 2500);
    return () => clearInterval(t);
  }, [images, load]);

  async function onUpload(file: File | null) {
    if (!file || !id) return;
    setBusy(true);
    setError(null);
    try {
      await api.uploadImage(id, file, {
        filename: file.name,
        alt,
        visibility,
        contentType: file.type || "image/png",
      });
      setAlt("");
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function openDetail(img: ProjectImage) {
    setBusy(true);
    setError(null);
    setSignedUrl(null);
    try {
      const { data } = await api.getImage(id, img.id);
      setSelected(data);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function patchVisibility(next: ImageVisibility) {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.patchImage(id, selected.id, {
        visibility: next,
      });
      setSelected(data);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function reprocess() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.reprocessImage(id, selected.id);
      const { data } = await api.getImage(id, selected.id);
      setSelected(data);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function transformRotate() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.transformImage(id, selected.id, { rotate: 90 });
      const { data } = await api.getImage(id, selected.id);
      setSelected(data);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function signMd() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.signImage(id, selected.id, {
        tier: "md",
        format: "webp",
        ttl_seconds: 3600,
      });
      setSignedUrl(`${apiBase()}${imageSignedDownloadPath(data.token)}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  async function remove(img: ProjectImage) {
    if (!confirm(`Delete image ${img.filename || img.id}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteImage(id, img.id);
      if (selected?.id === img.id) setSelected(null);
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (loading && images.length === 0) {
    return <Spinner label="Loading images…" />;
  }

  return (
    <div>
      <PageHeader
        eyebrow="Storage"
        title="Images"
        description="Web-optimized project media: upload, async compress (AVIF + WebP + JPEG), view ACLs, bounded transform, and delete."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to={ROUTES.storage(id)}
              className="text-xs text-text-muted underline-offset-2 hover:underline"
            >
              Objects
            </Link>
            <Badge variant="secondary" className="gap-1">
              <ImageIcon className="h-3 w-3" strokeWidth={1.75} />
              {images.length} image{images.length !== 1 ? "s" : ""}
            </Badge>
          </div>
        }
      />

      <div className="mb-4 flex gap-2 border-b border-border pb-2">
        <Link
          to={ROUTES.storage(id)}
          className="rounded-md px-3 py-1.5 text-sm text-text-muted hover:bg-surface-container"
        >
          Objects
        </Link>
        <span className="rounded-md bg-surface-container px-3 py-1.5 text-sm font-medium text-text-primary">
          Images
        </span>
      </div>

      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="card mb-6 p-5">
        <h2 className="mb-3 flex items-center gap-2 font-sans text-sm font-semibold text-text-primary">
          <Upload className="h-4 w-4" strokeWidth={1.75} />
          Upload image
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            Visibility
            <select
              className="input text-sm"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as ImageVisibility)}
            >
              {VIS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            Alt text
            <input
              className="input min-w-[12rem] text-sm"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Hero photo"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-muted">
            File
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
              className="text-sm"
              disabled={busy}
              onChange={(e) => void onUpload(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        <p className="mt-2 text-xs text-text-muted">
          Max 20 MiB. Compress runs asynchronously (status pending → ready).
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          Status filter
          <select
            className="input text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="pending">pending</option>
            <option value="processing">processing</option>
            <option value="ready">ready</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void load()}
        >
          <RefreshCw className="mr-1 h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {images.length === 0 ? (
        <EmptyState title="No images yet">
          Upload a JPEG, PNG, WebP, or GIF to create the first image record.
        </EmptyState>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Status</th>
                  <th>Visibility</th>
                  <th>Size</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {images.map((img) => (
                  <tr key={img.id}>
                    <td>
                      <button
                        type="button"
                        className="text-left text-sm text-primary hover:underline"
                        onClick={() => void openDetail(img)}
                      >
                        {img.filename || img.id.slice(0, 8)}
                      </button>
                    </td>
                    <td>
                      <Badge
                        variant={
                          img.status === "ready"
                            ? "default"
                            : img.status === "failed"
                              ? "danger"
                              : "secondary"
                        }
                      >
                        {img.status}
                      </Badge>
                    </td>
                    <td className="font-mono text-xs">{img.visibility}</td>
                    <td className="text-xs text-text-muted">
                      {(img.size_bytes / 1024).toFixed(1)} KiB
                    </td>
                    <td>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => void remove(img)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card p-5">
            {!selected ? (
              <p className="text-sm text-text-muted">
                Select an image to inspect variants, change visibility, reprocess,
                transform, or mint a signed URL.
              </p>
            ) : (
              <div className="space-y-3">
                <h2 className="font-sans text-sm font-semibold text-text-primary">
                  {selected.filename}
                </h2>
                <p className="font-mono text-[11px] text-text-muted">
                  {selected.id}
                </p>
                <p className="text-xs text-text-muted">
                  {selected.width ?? "?"}×{selected.height ?? "?"} ·{" "}
                  {selected.content_type} · {selected.status}
                </p>
                {selected.error_message ? (
                  <p className="text-xs text-destructive">{selected.error_message}</p>
                ) : null}
                <label className="flex flex-col gap-1 text-xs text-text-muted">
                  Visibility
                  <select
                    className="input text-sm"
                    value={selected.visibility}
                    disabled={busy}
                    onChange={(e) =>
                      void patchVisibility(e.target.value as ImageVisibility)
                    }
                  >
                    {VIS.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void reprocess()}
                  >
                    Reprocess
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void transformRotate()}
                  >
                    Rotate 90°
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void signMd()}
                  >
                    Sign md/webp URL
                  </Button>
                </div>
                {signedUrl ? (
                  <div className="rounded border border-primary/30 p-2">
                    <code className="block break-all font-mono text-[11px] text-text-muted">
                      {signedUrl}
                    </code>
                  </div>
                ) : null}
                <div>
                  <h3 className="mb-1 text-xs font-semibold text-text-primary">
                    Variants ({selected.variants?.length ?? 0})
                  </h3>
                  <ul className="max-h-48 space-y-1 overflow-auto text-xs text-text-muted">
                    {(selected.variants ?? []).map((v) => (
                      <li key={v.id} className="font-mono">
                        {v.tier}.{v.format} · {v.width}×{v.height} ·{" "}
                        {(v.size_bytes / 1024).toFixed(1)} KiB
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
